import {
  Flex,
  FlexColumn,
  ListTitleBar,
  StatusContainerSuccess,
  listHeaderCss,
  useDeleteAlert,
  InputGroupBreak,
  InputGroup,
  InputRow,
  ModelDateDMY,
  EmptyInput,
  AddressSearch,
  flattenErrorTypeArray,
  IconButton,
} from '@equifinance/shared';
import styled from 'styled-components';
import moment from 'moment';
import { Fragment } from 'react';
import { SectionTitleIcon } from '../../Icons/SectionTitleIcon';
import { Stages } from '../../../../types/Stages';
import { StaticDataItem } from '../../../../api/useAccountApi';
import {
  DeleteIcon,
  TickCircleIcon,
} from '../../components/Icons';
import { DatedAddress } from '../../../../contexts/account/Account';
import { useAccountContext } from '../../../../contexts';
import Section from '../../components/Section';

const AddressListTitleBar = styled(ListTitleBar)`${({ theme }) => `
  justify-content:start;
  gap: ${theme.padding[4]};
  padding: ${theme.padding[2]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
  color: ${theme.colours.neutral[2]};
`}`;

const DateTitle = styled.div`${({ theme }) => `
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.colours.primary[1]};
`}`;

const AddressListDateContainer = styled(FlexColumn)`${({ theme }) => `
  width: 120px;
  border-left: ${theme.borderWidth[1]} solid ${theme.colours.neutral[7]};
  padding-left: ${theme.padding[2]};
`}`;

const AddressListAddressContainer = styled.div`
  flex-grow: 1;
`;
const DefaultAddressText = styled.div`${({ theme }) => `
  color:${theme.colours.neutral[4]};
  line-height:${theme.fontSize[4]};
`}`;

const DeleteAddressContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 40px;
`;

export default function AddressHistoryItem({
  address,
  addressIndex,
  collapsed,
  applicantIndex,
  countries,
  lendingRegions,
  onBlur = () => {},
}: {
  address: DatedAddress,
  addressIndex: number,
  collapsed: boolean,
  applicantIndex: number,
  countries: StaticDataItem[],
  lendingRegions: StaticDataItem[],
  onBlur?: () => void,
}) {
  const {
    account, deleteFromAccountArray, creditSearchCompleted,
  } = useAccountContext();

  const {
    stage, errors, warnings,
  } = account;

  const deleteAlert = useDeleteAlert();

  const onClickDeleteAddress = (addressId: string) => {
    deleteAlert({
      onConfirm: () => deleteFromAccountArray(`applicants[${applicantIndex}].addresses`, addressId),
      title: 'Delete',
      text: 'Are you sure you want to delete this address?',
    });
  };

  const getAddressTitle = (address: DatedAddress) => {
    const addressTitle = `${address?.address?.subBuilding ? `${address?.address?.subBuilding}, ` : ''}`
      + `${address?.address?.houseName ? `${address?.address?.houseName}, ` : ''}`
      + `${address?.address?.houseNumber ? `${address?.address?.houseNumber}, ` : ''}`
      + `${address?.address?.street ? `${address?.address?.street}, ` : ''}`
      + `${address?.address?.postTown ? `${address?.address?.postTown}, ` : ''}`
      + `${address?.address?.region ? `${address?.address?.region}, ` : ''}`
      + `${address?.address?.postCode ? `${address?.address?.postCode}` : ''}`;

    return addressTitle === ''
      ? (
        <>
          <DefaultAddressText>
            Applicant&apos;s address
          </DefaultAddressText>
          <DefaultAddressText>
            will be displayed here
          </DefaultAddressText>
        </>
      )
      : addressTitle;
  };

  const combinedErrors = flattenErrorTypeArray([
    ...(errors?.[address?.id] ?? []),
    ...(errors?.[address?.address?.id] ?? []),
  ]);

  const handleInputTouched = () => {
    onBlur();
  };

  return (
    <Fragment key={address?.id}>
      <Section
        key={address?.id}
        id={address?.id}
        collapsed={collapsed}
        level={2}
        headerCss={listHeaderCss}
        errors={errors}
        warnings={warnings}
        title={(
          <AddressListTitleBar>

            <AddressListAddressContainer>
              {getAddressTitle(address)}
            </AddressListAddressContainer>

            <AddressListDateContainer>
              <DateTitle>Moved in</DateTitle>
              {address?.movedInDate
                ? moment(address?.movedInDate).format('MMMM YYYY')
                : <>-</>}
            </AddressListDateContainer>

            <AddressListDateContainer>
              { address?.isCurrentAddress && (
              <StatusContainerSuccess>
                Current address
              </StatusContainerSuccess>
              )}

              { !address?.isCurrentAddress && (
              <>
                <DateTitle>Moved out</DateTitle>
                <>
                  { address?.movedOutDate
                    ? moment(address?.movedOutDate).format('MMMM YYYY')
                    : <>-</>}
                </>
              </>
              )}
            </AddressListDateContainer>

            <DeleteAddressContainer>
              {(!address?.isCurrentAddress && !creditSearchCompleted) && (
              <IconButton onClick={() => onClickDeleteAddress(address?.id)}>
                <DeleteIcon />
              </IconButton>
              )}
            </DeleteAddressContainer>
          </AddressListTitleBar>
        )}
        titleIcon={(
          <SectionTitleIcon
            errors={combinedErrors}
            tickIcon={<TickCircleIcon />}
          />
        )}
      >
        <AddressSearch
          field={`applicants[${applicantIndex}].addresses[${addressIndex}].address`}
          addressId={address?.address?.id}
          countries={(address?.isCurrentAddress ? lendingRegions : countries)?.reduce((prev, country) => ({
            ...prev,
            [country?.key]: country?.label,
          }), {} as Record<string, string>)}
          disabled={stage !== Stages.CreditDecision || creditSearchCompleted}
          limitToGreatBritain={address?.isCurrentAddress}
          errors={errors}
          searchPlaceholder={`Enter postcode or first line of ${(address?.isCurrentAddress ? 'current' : 'previous')} address`}
          subBuildingLabel="Flat number"
          buildingNameLabel="House name"
          buildingNumberLabel="House number"
          onBlur={handleInputTouched}
        />

        <InputGroupBreak />

        <InputGroup>
          <InputRow>
            <ModelDateDMY
              label="Moved in"
              field={`applicants[${applicantIndex}].addresses[${addressIndex}].movedInDate`}
              disabled={creditSearchCompleted}
              required
              onBlur={handleInputTouched}
            />
            { !address?.isCurrentAddress
              ? (
                <ModelDateDMY
                  label="Moved out"
                  field={`applicants[${applicantIndex}].addresses[${addressIndex}].movedOutDate`}
                  disabled={creditSearchCompleted}
                  required
                  onBlur={handleInputTouched}
                />
              )
              : <EmptyInput />}
          </InputRow>
        </InputGroup>
      </Section>
    </Fragment>

  );
}
